import { useEffect, useMemo, useRef } from "react";
import { Flex } from "styles/utility-components";
import useDeepCompareEffect from "use-deep-compare-effect";

import { Currency } from "components/app_layout/Typography";
import { NoticeActions } from "components/notices/NoticeActions";
import { mapNoticeForActions, NoticeFilters } from "components/notices/utils";
import { NewTitleCell } from "components/table_components/NewTitleCell";
import SuppliersContainer from "components/table_components/SuppliersContainer";
import RedactData from "components/ui/redact_data/RedactData";
import NoticeStage from "lib/core_components/NoticeStage";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { Table } from "lib/core_components/Table/Table";
import { NoticesDto } from "lib/hooks/api/notices/useNotices";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import { ProHelperDataTypes } from "lib/providers/ProHelper";
import { SortState } from "lib/search/types";
import { EventNames, useTracking } from "lib/tracking";
import { NoticeSemanticColumnField } from "lib/types/savedViews";
import { formatDate, formatDateOrOpenEnded } from "lib/utils";
import { SaveLeadDrawerProvider } from "../leads/saving/SaveLeadDrawerProvider";
import { ViewLeadsDrawerProvider } from "../leads/viewing/ViewLeadsDrawerProvider";
import { NoticeV2Filters, NoticeV2FiltersSchema, noticeV2FiltersToNoticeFilters } from "./utils";

const titleColumnMapping: Record<NoticeSemanticColumnField, string> = {
  relevanceScore: "Score",
  title: "Title & buyer",
  stage: "Stage",
  value: "Value",
  suppliers: "Supplier(s)",
  actions: "Actions",
  awardDate: "Award Date",
  closeDate: "Close Date",
  expiryDate: "Expiry Date",
  publishDate: "Publish Date",
};

function getNoticeColumnsV2(
  filters: NoticeFilters,
  columnSettings: NoticeSemanticColumnField[],
  requiredDataType: ProHelperDataTypes | undefined,
): ColumnType<NoticesDto>[] {
  const title: ColumnType<NoticesDto> = {
    ...commonTableColumns.titleColumn,
    title: titleColumnMapping.title,
    key: "title",
    dataIndex: "name",
    render: (_, record) => (
      <NewTitleCell notice={record} requiredDataType={requiredDataType} filters={filters} />
    ),
  };

  const stage: ColumnType<NoticesDto> = {
    ...commonTableColumns.stageColumn,
    title: titleColumnMapping.stage,
    dataIndex: "stage",
    key: "stage",
    render: (_, record) => {
      return <NoticeStage stage={record.stagev2 ?? undefined} />;
    },
    showSorterTooltip: false,
  };

  const value: ColumnType<NoticesDto> = {
    ...commonTableColumns.valueColumn,
    title: titleColumnMapping.value,
    dataIndex: "value",
    key: "value",
    render: (_, r) => (
      <Flex justifyContent="end">
        <RedactData isRedacted={r.redacted}>
          <Currency longFormat value={r.value} currency={r.currency || undefined} />
        </RedactData>
      </Flex>
    ),
  };

  const closeDate: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    title: titleColumnMapping.closeDate,
    dataIndex: "closeDate",
    key: "closeDate",
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>{formatDateOrOpenEnded(r.closeDate)}</RedactData>
    ),
  };

  const publishDate: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    title: titleColumnMapping.publishDate,
    key: "publishDate",
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>
        {r.publishDate ? formatDate(new Date(r.publishDate)) : r.publishDate}
      </RedactData>
    ),
  };

  const awardDate: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    title: titleColumnMapping.awardDate,
    key: "awardDate",
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>
        {r.awardDate ? formatDate(new Date(r.awardDate)) : r.awardDate}
      </RedactData>
    ),
  };

  const expiryDate: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    title: titleColumnMapping.expiryDate,
    key: "expiryDate",
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>{formatDateOrOpenEnded(r.expiryDate)}</RedactData>
    ),
  };

  const actions: ColumnType<NoticesDto> = {
    // Uses the qualification preset for consistent sizing and overflow handling with NoticeTable's qualification column
    ...commonTableColumns.qualification,
    title: titleColumnMapping.actions,
    key: "actions",
    showSorterTooltip: false,
    render: (_, r) => {
      return (
        <RedactData isRedacted={r.redacted} bullets={12}>
          <NoticeActions notice={mapNoticeForActions(r)} contextSource="Notices 2.0" size="small" />
        </RedactData>
      );
    },
  };

  // const country: ColumnType<NoticesDto> = {
  //   ...commonTableColumns.countryColumn,
  //   title: getNoticeColumnCopy("country"),
  //   key: "country",
  //   render: (_, r) => <RedactData isRedacted={r.redacted}>{r.country}</RedactData>,
  // };

  // const is_sme: ColumnType<NoticesDto> = {
  //   ...commonTableColumns.isSMEColumn,
  //   title: (
  //     <div>
  //       SME suppliers?{" "}
  //       <InfoButton
  //         note={
  //           <div>
  //             For contracts awarded to multiple suppliers, this will show "Yes" if any of the
  //             suppliers are SMEs
  //           </div>
  //         }
  //       />
  //     </div>
  //   ),
  //   key: "isSme",
  //   render: (_, r) => (
  //     <RedactData isRedacted={r.redacted}>
  //       {r.stage === NoticesStages.Awarded
  //         ? r.suppliers?.some((s) => s.isSme)
  //           ? "Yes"
  //           : "No"
  //         : "N/A"}
  //     </RedactData>
  //   ),
  // };

  const suppliers: ColumnType<NoticesDto> = {
    ...commonTableColumns.supplierColumn,
    title: titleColumnMapping.suppliers,
    key: "suppliers",
    render: (_, r) => {
      return (
        <RedactData isRedacted={r.redacted} bullets={12}>
          <SuppliersContainer recordName={r.name ?? "--"} suppliers={r.suppliers || []} />
        </RedactData>
      );
    },
  };

  // const buyer_categories: ColumnType<NoticesDto> = {
  //   key: "buyer_type",
  //   title: getNoticeColumnCopy("buyer_categories"),
  //   width: "210px",
  //   render: (r: NoticesDto) => (
  //     <RedactData isRedacted={r.redacted}>
  //       <MultipleEntityLinks
  //         title={`Types for ${r.buyers.map((b) => b.name).join(", ")}`}
  //         links={r.buyers.map((b) => (
  //           <BuyerCategoriesWithPopover
  //             size="default"
  //             buyerCategories={b.categoryAssignments.map((assignment) => ({
  //               buyer_category_id: assignment.categoryId,
  //               source: assignment.source,
  //             }))}
  //             buyerName={b.name}
  //             key={b.guid}
  //           />
  //         ))}
  //       />
  //     </RedactData>
  //   ),
  // };

  // const framework: ColumnType<NoticesDto> = {
  //   title: getNoticeColumnCopy("framework"),
  //   key: "framework",
  //   sizeConfig: {
  //     xlarge: 300,
  //     large: 250,
  //     medium: 200,
  //     small: 170,
  //   },
  //   showSorterTooltip: false,
  //   render: (_, r) => {
  //     return r.relatedFrameworkProcess ? (
  //       <FrameworkCell
  //         frameworkId={r.relatedFrameworkProcess.frameworkId}
  //         frameworkName={r.relatedFrameworkProcess.frameworkName}
  //         processType={r.relatedFrameworkProcess.processType}
  //         filters={filters}
  //       />
  //     ) : null;
  //   },
  // };

  // const indicator: ColumnType<NoticesDto> = {
  //   title: getNoticeColumnCopy("indicator"),
  //   key: "signals",
  //   sizeConfig: {
  //     xlarge: 250,
  //     large: 150,
  //     medium: 150,
  //     small: 150,
  //   },
  //   showSorterTooltip: false,
  //   render: (_, r) => {
  //     return (
  //       <RedactData isRedacted={r.redacted} bullets={12}>
  //         <SignalsContainer signals={r.signals || []} contextSource="In-row" />
  //       </RedactData>
  //     );
  //   },
  // };

  const columnTypeMap: Partial<Record<NoticeSemanticColumnField, ColumnType<NoticesDto>>> = {
    title,
    stage,
    value,
    suppliers,
    actions,
    awardDate,
    expiryDate,
    closeDate,
    publishDate,
  };

  const columns: ColumnType<NoticesDto>[] = [];

  for (const column of columnSettings) {
    const columnType = columnTypeMap[column];
    if (columnType) {
      columns.push(columnType);
    }
  }

  return columns;
}

/** Default table pagination; `page` matches GraphQL `PaginationOptions` / URL state helpers. */
export const DEFAULT_PAGINATION = { page: 1, pageSize: 10 };

/**
 * Cap on the `total` we pass to antd's `Table` `pagination` prop, used to
 * compute `pages = ceil(total / pageSize)` and render page links. Per
 * DATA-1572, the backend's `hits.total.value` is an OS per-shard counter
 * driven primarily by KNN `k`, NOT the unique-paginatable count.
 *
 * Set to OpenSearch's `index.max_result_window` (10000) — the actual hard
 * ceiling on `from + size`. Pages past `paginationDepth/pageSize` may have
 * less precise scoring (the per-shard candidate pool / normalisation
 * basis is exceeded), but all hits are still real matches the user has
 * access to (they're the same docs the export retrieves). Hiding them
 * from pagination while exposing them via export would be inconsistent.
 *
 * If a variant's `k × num_shards` makes `totalResults` exceed 10000, the
 * raw count is clamped here. Past 10000 OS itself errors on `from + size`
 * so there's nothing to render anyway.
 */
const PAGINATION_TOTAL_CAP = 10000;

/** Columns for the homepage "Find expiring contracts" card when relevancy new-users is enabled. */
export const FIND_EXPIRING_CONTRACTS_RELEVANCY_COLUMN_FIELDS: NoticeSemanticColumnField[] = [
  "title",
  "expiryDate",
  "value",
  "suppliers",
  "actions",
];

/** Columns for the homepage "Find open tenders" card when relevancy new-users is enabled. */
export const FIND_OPEN_TENDERS_RELEVANCY_COLUMN_FIELDS: NoticeSemanticColumnField[] = [
  "title",
  "stage",
  "closeDate",
  "value",
  "actions",
];

/** Columns for the homepage "Analyse the market" card when relevancy new-users is enabled. */
export const ANALYSE_THE_MARKET_RELEVANCY_COLUMN_FIELDS: NoticeSemanticColumnField[] = [
  "title",
  "stage",
  "value",
  "actions",
  "publishDate",
];

/**
 * NoticeTableV2 - A notices table that uses NoticeV2Filters.
 */
export function NoticeTableV2({
  filters,
  columnSettings,
  onSortChange: _onSortChange,
  selectedRows,
  onSelectedRowsChange,
  requiredDataType,
  emptyText,
  results = [],
  totalResults = 0,
  isLoading = false,
  isError = false,
  currentPage = 1,
  pageSize = DEFAULT_PAGINATION.pageSize,
  onPageChange,
}: {
  filters: NoticeV2Filters;
  columnSettings: NoticeSemanticColumnField[];
  onSortChange?: (sort: SortState) => void;
  selectedRows?: string[];
  onSelectedRowsChange?: (selectedRowKeys: string[], selectedRows: NoticesDto[]) => void;
  requiredDataType?: ProHelperDataTypes;
  emptyText?: JSX.Element;
  results?: NoticesDto[];
  totalResults?: number;
  isLoading?: boolean;
  isError?: boolean;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, newPageSize: number) => void;
}) {
  const { logEvent } = useTracking();

  const filterChangedRef = useRef(false);
  const prevIsLoadingRef = useRef(isLoading);

  useDeepCompareEffect(() => {
    filterChangedRef.current = true;
  }, [filters]);

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading && filterChangedRef.current) {
      filterChangedRef.current = false;
      logEvent(EventNames.topicSearchResultsReturned, {
        "Context source": "Notices 2.0",
        "Total results": totalResults,
      });
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, totalResults, logEvent]);

  const noticeFilters = useMemo(
    () => noticeV2FiltersToNoticeFilters(NoticeV2FiltersSchema.parse(filters)),
    [filters],
  );
  const columns = useMemo(
    () => getNoticeColumnsV2(noticeFilters, columnSettings, requiredDataType),
    [noticeFilters, columnSettings, requiredDataType],
  );

  useDeepCompareEffect(() => {
    onSelectedRowsChange?.([], []);
  }, [filters]);

  const { viewRecord } = useRestricedViewNotice(requiredDataType);
  const { viewRecord: showPaywallModal } = useRestricedViewNotice("Row-click", "AWARDS");

  return (
    <SaveLeadDrawerProvider trackingData={{ "Context source": "In-row" }}>
      <ViewLeadsDrawerProvider>
        <Table<NoticesDto>
          ariaLabel="Notice Table"
          dataSource={results}
          loading={isLoading}
          columns={columns}
          showSorterTooltip={false}
          rowKey={(record) => record.procurementStage.id}
          onRow={(record) => ({
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
              if (record.redacted) {
                showPaywallModal(record.guid);
              } else {
                viewRecord(record.guid);
              }
            },
          })}
          pagination={{
            current: currentPage,
            pageSize,
            // Clamp to PAGINATION_TOTAL_CAP — see comment on the constant.
            // Without this, antd renders thousands of unreachable page
            // links and the user-visible "totalCount keeps growing as I
            // paginate" treadmill bug from DATA-1572 manifests.
            total: Math.min(totalResults, PAGINATION_TOTAL_CAP),
            onChange: (page, newPageSize) => {
              onPageChange?.(page, newPageSize ?? pageSize);
              logEvent(EventNames.paginationSelected, { Value: page });
            },
            showSizeChanger: true,
          }}
          onChange={(pag, __, sorter) => {
            if (currentPage !== pag.current || pageSize !== pag.pageSize) {
              return;
            }
            const sorterObj = Array.isArray(sorter) ? sorter[0] : sorter;
            if (!sorterObj?.order) {
              return;
            }
            // const newSortState: SortState = {
            //   order: sorterObj.order === "descend" ? "DESC" : "ASC",
            //   field: sorterObj.columnKey as string,
            // };
            // if (!isEqual(filters.sort, newSortState)) {
            //   onSortChange?.(newSortState);
            //   logEvent(EventNames.tableSorted, {
            //     Context: "Column action",
            //     "Sorted by": startCase(newSortState.field),
            //     "Sort order": newSortState.order,
            //   });
            // }
          }}
          rowSelection={
            onSelectedRowsChange &&
            selectedRows && {
              onChange: (selectedRowKeys, selectedRowsData) => {
                onSelectedRowsChange(selectedRowKeys as string[], selectedRowsData);
              },
              preserveSelectedRowKeys: true,
              selectedRowKeys: selectedRows,
            }
          }
          isError={isError}
          locale={{
            emptyText,
          }}
        />
      </ViewLeadsDrawerProvider>
    </SaveLeadDrawerProvider>
  );
}
